import * as React from 'react';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { Decks } from '../../components/Decks/Decks';
import { CollapsibleDropDown } from '../../components/CollapsibleDropDown/CollapsibleDropDown';
import { CollapsibleDropDownTitle } from '../../components/CollapsibleDropDown/CollapsibleDropDownTitle';
import { FurtherInfo } from '../../components/FurtherInfo/FurtherInfo';
import { DrinkIcon } from '../../components/Icons/newIcons/DrinkIcon';
import { DashboardIcon } from '../../components/Icons/newIcons/DashboardIcon';
import { RouteIcon } from '../../components/Icons/newIcons/RouteIcon';
import { ShipIcon } from '../../components/Icons/newIcons/ShipIcon';
import { StarIcon } from '../../components/Icons/newIcons/StarIcon';
import { LevelIcon } from '../../components/Icons/newIcons/LevelIcon';
import { InfoFilledIcon } from '../../components/Icons/newIcons/InfoFilledIcon';
import ReviewsDesktop from '../../components/DesktopReviews/DesktopReviews';
import { ReviewsGoogleScript } from '../../components/DesktopReviews/ReviewsGoogleScript';
import { RequestForm } from '../../components/RequestForm/RequestForm';
import { CruiseInquiryForm } from '../../components/CruiseInquiryForm/CruiseInquiryForm';
import { Specials } from '../../components/Specials/Specials';
import { TrackComponent } from '../../components/TrackComponent';
import { getStockPrice } from '../../components/Urgency';
import { getHarbourEmbarkations, getRequestFormBtnProps } from '../../helpers/cruisePageHelpers';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { hasSoldOut, AvailabilityTypes } from '../../helpers/priceHelpers';
import { getSailNidFromHash } from '../../helpers/routing';
import { __ } from '../../helpers/TranslationService';
import { useActions } from '../../hooks/useActions';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import CabinsLeftLabel from './components/CabinsLeftLabel';
import CruiseHeader from './components/CruiseHeader';
import { CruiseOverview } from './components/CruiseOverview';
import CruisePriceSection from './components/CruisePriceSection';
import CruisePriceSectionMobile from './components/CruisePriceSectionMobile';
import InclusivesDesktop, { InclusivesText } from './components/InclusivesText/InclusivesText';
import Waypoints from './components/Waypoints';
import { CruiseStage } from './CruiseStage/CruiseStage';
import Gallery, { mapMediaToGallery } from '../../components/Gallery/Gallery';
import useWindowSize, { IViewPortSize } from '../../hooks/useViewport';
import { isDesktop as isBrowserDesktop } from '../../helpers/responsiveHelpers/responsiveHelpers';
import { checkDirectBookingAvailability } from '../../helpers/directBookingMarketSupported';
import DreamLinesPackageBanner from './components/DreamLinesPackageBanner';
import { LegalText } from './components/LegalText';
import loadable from '@loadable/component';
import { MarketContext } from '../../context/markets';
import { CruiseInquiryPhoneCardV2 } from '../../components/CruiseInquiryForm/CruiseInquiryPhoneCardV2';
import { ICabinInfo } from '../../interfaces';
import { cruisePage as cruisePageType } from '../../consts/pageTypes';

const DreamDeal = loadable(() => import('../../components/DreamDeal/DreamDeal'));

const category = 'mobile-productpage';
const cruiseOverview = 'CRUISEOVERVIEW';
const cruiseRoute = 'CRUISEROUTE';
const cruiseInclusive = 'CRUISEINCLUSIVE';
const cruiseShip = 'CRUISESHIP';
const cruiseDecks = 'CRUISEDECKS';
const cruiseReviews = 'CRUISEREVIEWS';
const additionalInfo = 'ADDITIONALINFO';

const DropDownIconWrapper = styled.div`
    fill: ${({ theme }) => theme.icon.brandColor1};
`;

const PhoneCardWrapper = styled.div`
    margin-top: 40px;
    padding: 0 16px;
`;

const DeckPlanWrapper = styled.div`
    margin: 0 -20px;
`;

const getInitiallySelectedCabin = (
    cabins: ICabinInfo[],
    cheapestCabinNidForSail: number,
    cabinNid?: number,
) => {
    const currentNid = cabinNid || cheapestCabinNidForSail;
    const selectedCabin = cabins.find((cabin) => cabin.cabinType.nid === currentNid) ?? null;
    const isSelectedCabinPriceNotValid =
        selectedCabin && selectedCabin.cabinType.price && !selectedCabin.cabinType.price?.valid;

    if (isSelectedCabinPriceNotValid) {
        // get the smallest non zero price cabin
        return cabins.reduce((prevCabin, currCabin) => {
            const isCurrPriceNonZero = currCabin.perPersonPrice !== 0;
            const isPrevCabinNullOrHigher =
                !prevCabin || prevCabin.perPersonPrice > currCabin.perPersonPrice;

            if (isCurrPriceNonZero && isPrevCabinNullOrHigher) {
                return currCabin;
            }
            return prevCabin;
        }, null);
    }

    return selectedCabin;
};

export const pushSailToDl = (saildId) => {
    if (typeof window === 'undefined') {
        return;
    }
    (window as any)?.dataLayer.push({
        event: 'selected-sail',
        saildId,
    });
};

export const pushCheapestPriceToDl = (cheapestPrice, adultsNum, childrenNum) => {
    if (cheapestPrice > 0 && typeof window !== 'undefined') {
        (window as any)?.dataLayer.push({
            event: 'cheapest-price',
            price: cheapestPrice,
            adultsNum,
            childrenNum,
        });
    }
};

const TrackableOverviewSection = TrackComponent(CollapsibleDropDown, {
    category,
    action: 'navigation',
    label: 'overview',
});
const TrackableRouteSection = TrackComponent(CollapsibleDropDown, {
    category,
    action: 'navigation',
    label: 'route',
});
const TrackableInclusivesSection = TrackComponent(CollapsibleDropDown, {
    category,
    action: 'navigation',
    label: 'inclusive',
});
const TrackableShipSection = TrackComponent(CollapsibleDropDown, {
    category,
    action: 'navigation',
    label: 'ship',
});

const CruisePage = () => {
    const storeState = useTypedSelector((state) => state);
    const { pageData, cruisePage, router, reviews: reviewsData, requestForm, abTests } = storeState;

    const { markets } = React.useContext(MarketContext);
    const routeSectionRef = React.useRef(null);

    const [selectedCabinId, setSelectedCabinId] = useState<number>();
    const [selectedCabinUrl, setSelectedCabinUrl] = useState('');
    const [isBookingFlow, setIsBookingFlow] = useState(false);
    const [isCruiseInquiryFlow, setIsCruiseInquiryFlow] = useState(false);
    const [forceOpenRoute, setForceOpenRoute] = useState(false);
    const [isSoldOut, setIsSoldOut] = useState(false);
    const { width }: IViewPortSize = useWindowSize();
    const [isDesktop, setIsDesktop] = useState(isBrowserDesktop(width));
    const {
        getRates,
        setSelectedSailIndex,
        checkForDirectBooking,
        updateRequestFormFromCookies,
        getPaxConfig,
        updateRequestFormFields,
    } = useActions();

    const setSelectedSail = () => {
        const selectedSailNid = getSailNidFromHash(window.location.hash);
        let selectedSailIndex = cruisePage.selectedSailIndex;
        if (selectedSailNid) {
            const sails = cruisePage.sails;
            selectedSailIndex = sails.findIndex((sail) => sail.nid === selectedSailNid);
            setSelectedSailIndex(selectedSailIndex);
        }
    };

    const handleCabinSelection = (cabinId) => {
        const cabinUrl = getRequestFormBtnProps(router.url, sails[selectedSailIndex], cabinId);
        setSelectedCabinUrl(cabinUrl);
        setSelectedCabinId(cabinId);
        updateRequestFormFields({ cabinNid: cabinId });
    };
    const { reviews } = reviewsData;
    const {
        ship,
        waypoints,
        sails,
        selectedSailIndex,
        soldOutBooking,
        minPrice,
        currency,
        packageCruises,
        loadingRates,
        cabins,
        specials,
        routeImage,
        isMapImageEnabled,
    } = cruisePage;

    // In order to get proper cabin we need to wait for rates to be loaded
    // Otherwise you may end up with cabin wich is sold out
    // So don't try to remove this cruisePage.loadingRates check
    const cabin = !cruisePage.loadingRates
        ? getInitiallySelectedCabin(
              cruisePage.cabins,
              cruisePage.cheapestCabinNidForSail,
              requestForm.cabinNid,
          )
        : null;

    useEffect(() => {
        if (cabin) {
            handleCabinSelection(cabin?.cabinType.nid);
        }
    }, [cabin]);

    // on mobile devices selected cabin is stored in state of this component
    // on desktop we get it from requestForm.cabinNid
    // someday maybe this will be unified 🤮
    const mobileFormCabinData = cabins?.find(
        (item) => item.cabinType.nid === Number(selectedCabinId),
    );

    const dreamDeal = specials.filter((el) => el.isDreamDeal)[0] || false;

    // on request means cabin is also availabe, so we can proceed to 2nd step on mobile form
    const isAvailableCabin =
        !!mobileFormCabinData?.perPersonPrice ||
        mobileFormCabinData?.cabinType.price?.type !== AvailabilityTypes.soldOut;

    const belowThresholdPriceMarkets = markets.isDreamlinesDE || markets.isCruiseAway;

    useEffect(() => {
        if (!isDesktop) {
            updateRequestFormFields({
                cabinNid: cabin?.cabinType?.nid,
                cruiseNid: cruisePage?.nid,
                sailNid: cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
                selectedSailIndex: cruisePage?.selectedSailIndex,
            });
        }
    }, [selectedSailIndex, mobileFormCabinData, isDesktop]);

    useEffect(() => {
        if (loadingRates || selectedSailIndex === -1) {
            return;
        }

        const desktopFormCabinData = cabins?.find(
            (item) => item.cabinType.nid === Number(requestForm.cabinNid),
        );

        if (!desktopFormCabinData) {
            return;
        }

        const selectedSail = sails[selectedSailIndex];
        const departure = selectedSail?.departure;

        const isDisabled = hasSoldOut(
            departure,
            soldOutBooking,
            desktopFormCabinData.perPersonPrice,
        );

        if (isDisabled) {
            eventTracker.trackG4Event('price_sold_out', {
                product_name: cruisePage.nid,
            });
        }
        setIsSoldOut(isDisabled);
    }, [loadingRates, selectedSailIndex, requestForm.cabinNid]);

    const embarkations = getHarbourEmbarkations({ waypoints });
    const iconProps = { width: 20, height: 20 };

    const overviewTitle = (
        <CollapsibleDropDownTitle title={__('overview', 'dreamlines')}>
            <DropDownIconWrapper>
                <DashboardIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );
    const inclusivesTitle = (
        <CollapsibleDropDownTitle title={__('included services', 'dreamlines')}>
            <DropDownIconWrapper>
                <DrinkIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );
    const shipTitle = (
        <CollapsibleDropDownTitle title={__('ship', 'dreamlines')}>
            <DropDownIconWrapper>
                <ShipIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );
    const routeTitle = (
        <CollapsibleDropDownTitle title={__('Itinerary', 'dreamlines')}>
            <DropDownIconWrapper>
                <RouteIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );
    const reviewsTitle = (
        <CollapsibleDropDownTitle title={__('%s Reviews', 'dreamlines', __('ship', 'dreamlines'))}>
            <DropDownIconWrapper>
                <StarIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );

    const additionalInfoTitle = (
        <CollapsibleDropDownTitle title={__('additionalInformation', 'dreamlines')}>
            <DropDownIconWrapper>
                <InfoFilledIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );

    const decksInfoTitle = (
        <CollapsibleDropDownTitle title={__('Decks', 'dreamlines')}>
            <DropDownIconWrapper>
                <LevelIcon {...iconProps} />
            </DropDownIconWrapper>
        </CollapsibleDropDownTitle>
    );

    const showReviewSection = Boolean(
        reviews?.aggregation?.data?.attributes?.overall &&
            parseInt(reviews.aggregation.data.attributes.overall.toString(), 10) > 0,
    );

    let isDisabled =
        sails[selectedSailIndex] &&
        hasSoldOut(sails[selectedSailIndex].departure, soldOutBooking, minPrice);

    if (!selectedCabinUrl || !isAvailableCabin) {
        isDisabled = true;
    }
    const stockPrice = getStockPrice({
        cruisePage: cruisePage,
        stockThreshold: pageData.stockThreshold,
        countryCode: pageData.appConfig.countryCode,
    });

    const packageCruise =
        packageCruises !== null && packageCruises.length > 0 ? packageCruises[0] : null;

    const setRouteExpanded = () => {
        setForceOpenRoute(true);

        routeSectionRef?.current?.scrollIntoView({ top: 'end', behavior: 'smooth' });
    };
    const hasAnySails = sails.length > 0;
    const showRequestForm = isDesktop && hasAnySails;

    const cabinSectionRef = React.useRef<HTMLDivElement>(null);
    const scrollToCabinSection = () => cabinSectionRef.current?.scrollIntoView();

    useEffect(() => {
        if (showRequestForm) {
            updateRequestFormFromCookies();
            getPaxConfig();
        }
        getRates();
        checkForDirectBooking();
        setSelectedSail();
    }, []);

    useEffect(() => {
        if (!requestForm.cabinNid || loadingRates) {
            return;
        }
        // Scroll to the cabin section if the back button is clicked on the page type
        // 'requestFormPage' const cabinSectionRef = React.useRef(null);
        // We are using loadingRates as useEffect dependency to know
        // that cruise data is ready to prevent layout shifts

        const backFromSecondPage = window.sessionStorage.getItem('backFromSecondRequestFormPage');
        if (
            !isDesktop &&
            backFromSecondPage &&
            router.pageType === cruisePageType &&
            !loadingRates
        ) {
            scrollToCabinSection();
            window.sessionStorage.removeItem('backFromSecondRequestFormPage');
        }
    }, [loadingRates]);

    useEffect(() => {
        setIsDesktop(isBrowserDesktop(width));
    }, [width]);

    const selectedRates = cruisePage?.rates?.filter(
        (rate) => rate.sail_nid === cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
    );
    const selectedRate = selectedRates?.find(
        ({ cabintype_id }) => cabintype_id === requestForm.cabinNid,
    );

    useEffect(() => {
        pushCheapestPriceToDl(
            mobileFormCabinData?.perPersonPrice || selectedRate?.cheapest_price?.per_person_price,
            requestForm.adultsNum,
            requestForm.childrenNum,
        );
    }, [selectedRate, mobileFormCabinData, requestForm]);

    useEffect(() => {
        const selectedSail = sails[selectedSailIndex];
        if (selectedSailIndex > -1) {
            pushSailToDl(selectedSail.nid);
        }
    }, [selectedSailIndex]);

    useEffect(() => {
        const isMarketSupportsBooking = checkDirectBookingAvailability(pageData);

        const isRateAvailable = !!selectedRate?.cheapest_price;
        const isEstimatedPrice = Boolean(selectedRate?.cheapest_price?.is_estimated_price);

        const newBookingFunnel =
            !isEstimatedPrice &&
            !isSoldOut &&
            isRateAvailable &&
            !cruisePage.operator.isDreamlinesPackage &&
            isMarketSupportsBooking;

        setIsBookingFlow(newBookingFunnel);
        setIsCruiseInquiryFlow(isMarketSupportsBooking);

        // event to make a screen recording of new flow in hotjar
        if (isCruiseInquiryFlow && window?.hj) {
            window.hj('event', 'newflow');
        }
    }, [cruisePage.operator.isDreamlinesPackage, selectedRate, isSoldOut]);

    const cruiseDecksMap = cruisePage.ship.decks.map((item) => {
        const mobileImage = item.highResolutionPlan || item.deckPlan.original.url;
        const deckImage = !isDesktop ? mobileImage : item.deckPlan.original.url;
        return {
            title: item.title,
            image: deckImage,
        };
    });

    const hasRouteImage = isMapImageEnabled && Boolean(routeImage);
    const showNewPhoneCard = isCruiseInquiryFlow && !isDesktop;

    return (
        <div className="cruisePageContainer">
            <div className="cruisePageBreadcrumbs">
                <Breadcrumbs
                    className="onlyTabletUp greyBackground"
                    breadcrumbs={cruisePage.breadcrumbs}
                />
            </div>
            <div className="cruiseHeader hideMobile">
                <CruiseStage
                    startHarbour={embarkations.start.city}
                    endHarbour={embarkations.end.title}
                    isDesktop={isDesktop}
                    currency={currency}
                    packageCruise={packageCruise}
                    countryCode={pageData.appConfig.countryCode}
                />
            </div>
            <ReviewsGoogleScript
                name={ship.title}
                reviewsType="shipReviews"
                ratingValue={reviews.aggregation.data.attributes.overall}
                reviewCount={reviews.aggregation.data.attributes.numRatings}
                offerCount={cruisePage.sails?.length ?? 0}
                lowPrice={cruisePage.minPrice ?? 0}
                priceCurrency={pageData.appConfig.defaultCurrency}
            />
            {packageCruise && (
                <DreamLinesPackageBanner
                    withMessage={false}
                    currency={currency}
                    onlyMobile={true}
                    packageCruise={packageCruise}
                    countryCode={pageData.appConfig.countryCode}
                />
            )}
            <div className="hideDesktopAndUp">
                <Gallery
                    images={mapMediaToGallery(
                        cruisePage.images,
                        cruisePage.thumbnails,
                        cruisePage.routeImage,
                        cruisePage.videoUrls,
                        cruisePage.videoThumbPaths,
                    )}
                    isDesktop={false}
                    actionButtonHandler={setRouteExpanded}
                    actionButtonText={__('Show Itinerary', 'dreamlines')}
                    showActionButtonFirstSlide={!hasRouteImage}
                    logo={cruisePage.company.logo}
                />

                {dreamDeal && (
                    <DreamDeal text={dreamDeal.description} countDownDate={dreamDeal.valid_to} />
                )}
            </div>
            <div className="hideDesktopAndUp">
                <CruiseHeader
                    title={cruisePage.title}
                    nid={cruisePage.nid}
                    ship={cruisePage.ship}
                    startHarbour={embarkations.start.city}
                    reviewsAttributes={reviews?.aggregation?.data.attributes}
                    operator={cruisePage.operator}
                    zone={cruisePage.mainRegion?.title}
                    nights={cruisePage.nights}
                    waypoints={cruisePage.waypoints}
                    companyNid={cruisePage.company.nid}
                    companyTitle={cruisePage.company.title}
                    isPreserveOriginalTitle={cruisePage.isPreserveOriginalTitle}
                />
            </div>
            <div className="hideMobile">
                {cruisePage.specials?.length > 0 && (
                    <Specials specials={cruisePage.specials as any} />
                )}
                <InclusivesDesktop
                    includedServices={cruisePage.includedServices}
                    optionalServices={cruisePage.optionalServices}
                    excludedServices={cruisePage.excludedServices}
                    notesAndHints={cruisePage.notesAndHints}
                />
            </div>
            <div className="hideMobile">
                <LegalText
                    isMobile={!isDesktop}
                    cancellationFeesDescription={cruisePage.ship.cancellationFeesDescription}
                />
                {isCruiseInquiryFlow && (
                    <CruiseInquiryForm
                        pageData={pageData}
                        cruisePage={cruisePage}
                        requestForm={requestForm}
                        startHarbour={embarkations.start.title}
                        isSoldOut={isSoldOut}
                        cabin={cabin}
                    />
                )}
                {!isCruiseInquiryFlow && (
                    <RequestForm
                        pageData={pageData}
                        cruisePage={cruisePage}
                        requestForm={requestForm}
                        startHarbour={embarkations.start.title}
                        cabin={cabin}
                    />
                )}
            </div>
            <CruisePriceSectionMobile
                startHarbour={embarkations.start.title}
                endHarbour={embarkations.end.title}
                requestFormBtnUrl={selectedCabinUrl}
            />
            <div className="cruisePageSection">
                <div id="cruiseTabsMobile">
                    <div className="hideDesktopAndUp">
                        <TrackableOverviewSection title={overviewTitle}>
                            <CruiseOverview
                                startHarbour={embarkations.start.title}
                                endHarbour={embarkations.end.title}
                            />
                        </TrackableOverviewSection>
                        <TrackableRouteSection
                            title={routeTitle}
                            lazyLoadChild={<Waypoints />}
                            isOpened={forceOpenRoute}
                            setIsOpened={setForceOpenRoute}
                            forwardRef={routeSectionRef}
                        />
                        <TrackableInclusivesSection title={inclusivesTitle}>
                            <InclusivesText
                                includedServices={cruisePage.includedServices}
                                optionalServices={cruisePage.optionalServices}
                                excludedServices={cruisePage.excludedServices}
                                notesAndHints={cruisePage.notesAndHints}
                            />
                        </TrackableInclusivesSection>
                        <TrackableShipSection title={shipTitle}>
                            <FurtherInfo description={ship.description || ''} />
                        </TrackableShipSection>
                        {cruiseDecksMap.length > 0 && (
                            <CollapsibleDropDown title={decksInfoTitle}>
                                <DeckPlanWrapper>
                                    <Decks decks={cruiseDecksMap} />
                                </DeckPlanWrapper>
                            </CollapsibleDropDown>
                        )}

                        {showReviewSection && (
                            <CollapsibleDropDown title={reviewsTitle}>
                                <ReviewsDesktop
                                    reviews={reviews}
                                    filterParams={`filterParams[shipNid]=${ship.nid}`}
                                    reviewsType={'shipReviews'}
                                    shipTitle={ship.title}
                                    googleScriptName={ship.title}
                                    collapseComments={true}
                                    disableGoogleScript
                                />
                            </CollapsibleDropDown>
                        )}

                        {cruisePage.ship.cancellationFeesDescription && (
                            <CollapsibleDropDown title={additionalInfoTitle}>
                                <LegalText
                                    isMobile={!isDesktop}
                                    cancellationFeesDescription={
                                        cruisePage.ship.cancellationFeesDescription
                                    }
                                />
                            </CollapsibleDropDown>
                        )}

                        <CruisePriceSection
                            startHarbour={embarkations.start.title}
                            endHarbour={embarkations.end.title}
                            onCabinSelection={(e) => handleCabinSelection(e)}
                            cheapestCabinId={cruisePage.cheapestCabinNidForSail}
                            selectedCabinId={requestForm.cabinNid}
                            cabinSectionRef={cabinSectionRef}
                            isNextButtonDisabled={isDisabled}
                            selectedCabinUrl={selectedCabinUrl}
                        />
                    </div>

                    <div className="hideMobile">
                        <Waypoints />
                    </div>

                    {showNewPhoneCard && (
                        <PhoneCardWrapper>
                            <CruiseInquiryPhoneCardV2
                                phoneNumber={pageData.brandConfig.phoneNumber}
                                officeHours={pageData.header.headerContent.officeHours}
                                companyTitle={cruisePage.company.title}
                                isDreamdeal={cruisePage.operator.isDreamlinesPackage}
                                isSoldOut={isSoldOut}
                            />
                        </PhoneCardWrapper>
                    )}
                    <div className="cruisePageBreadcrumbs">
                        <Breadcrumbs
                            className="container onlyMobile"
                            breadcrumbs={cruisePage.breadcrumbs}
                        />
                    </div>
                </div>
            </div>
            <div className="hideMobile">
                {!!stockPrice && <CabinsLeftLabel cabinsLeft={stockPrice} />}
            </div>
        </div>
    );
};
export default CruisePage;
